import { useState, useMemo, useCallback, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { FolderOpen, Folder, ChevronRight, ChevronDown, Search, XIcon } from 'lucide-react'
import type { MediaFolder } from '@/types'

interface FolderTreePickerProps {
  folders: MediaFolder[]
  selectedId: string
  onSelect: (folderId: string) => void
  className?: string
  /** Custom count display per folder. Defaults to images.length + "img" */
  countFn?: (folder: MediaFolder) => { count: number; label: string }
}

export function FolderTreePicker({
  folders,
  selectedId,
  onSelect,
  className,
  countFn,
}: FolderTreePickerProps) {
  const [search, setSearch] = useState('')
  const [tagFilter, setTagFilter] = useState('all')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const rootFolders = useMemo(() => folders.filter(f => !f.parentId), [folders])

  const getChildren = useCallback((parentId: string) => {
    return folders.filter(f => f.parentId === parentId)
  }, [folders])

  // All unique tags
  const allTags = useMemo(() => {
    const tags = new Set<string>()
    folders.forEach(f => f.tags?.forEach(t => tags.add(t)))
    return Array.from(tags).sort()
  }, [folders])

  const isFiltering = !!search || tagFilter !== 'all'

  const filteredFolders = useMemo(() => {
    if (!isFiltering) return []
    return folders.filter(f => {
      if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false
      if (tagFilter !== 'all' && !(f.tags || []).includes(tagFilter)) return false
      return true
    })
  }, [folders, search, tagFilter, isFiltering])

  // Auto-expand ancestors of selected folder on mount
  useEffect(() => {
    if (!selectedId) return
    const ancestors: string[] = []
    let id: string | null | undefined = selectedId
    while (id) {
      const folder = folders.find(f => f.id === id)
      if (!folder || !folder.parentId) break
      ancestors.push(folder.parentId)
      id = folder.parentId
    }
    if (ancestors.length > 0) {
      setExpanded(prev => {
        const next = new Set(prev)
        ancestors.forEach(a => next.add(a))
        return next
      })
    }
  }, []) // Only on mount

  const toggleExpand = (folderId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(folderId)) next.delete(folderId)
      else next.add(folderId)
      return next
    })
  }

  const getCount = (folder: MediaFolder) => {
    if (countFn) return countFn(folder)
    return { count: folder.images.length, label: 'img' }
  }

  function renderTreeItem(folder: MediaFolder, depth: number) {
    const children = getChildren(folder.id)
    const hasChildren = children.length > 0
    const isExpanded = expanded.has(folder.id)
    const isSelected = folder.id === selectedId
    const { count, label } = getCount(folder)

    return (
      <div key={folder.id}>
        <button
          type="button"
          onClick={() => onSelect(folder.id)}
          className={cn(
            'w-full flex items-center h-7 text-[11px] pr-3 transition-colors',
            isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50',
          )}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
        >
          {hasChildren ? (
            <span
              role="button"
              className="flex h-4 w-4 shrink-0 items-center justify-center rounded hover:bg-muted"
              onClick={(e) => toggleExpand(folder.id, e)}
            >
              {isExpanded
                ? <ChevronDown className="h-3 w-3 text-muted-foreground" />
                : <ChevronRight className="h-3 w-3 text-muted-foreground" />
              }
            </span>
          ) : (
            <span className="w-4 shrink-0" />
          )}

          {isExpanded && hasChildren ? (
            <FolderOpen className={cn('h-3.5 w-3.5 shrink-0 mr-1', isSelected ? 'text-primary' : 'text-muted-foreground')} />
          ) : (
            <Folder className={cn('h-3.5 w-3.5 shrink-0 mr-1', isSelected ? 'text-primary' : 'text-muted-foreground')} />
          )}

          <span className="truncate flex-1 min-w-0 text-left font-medium">{folder.name}</span>

          {folder.tags && folder.tags.length > 0 && (
            <span className="hidden sm:flex items-center gap-0.5 shrink-0 mr-0.5">
              {folder.tags.slice(0, 2).map(tag => (
                <span key={tag} className="text-[7px] bg-muted px-1 rounded text-muted-foreground leading-tight">
                  {tag}
                </span>
              ))}
            </span>
          )}

          {count > 0 && (
            <span className="text-[9px] text-muted-foreground/70 shrink-0 tabular-nums ml-0.5">
              {count} {label}
            </span>
          )}
        </button>

        {isExpanded && children.map(child => renderTreeItem(child, depth + 1))}
      </div>
    )
  }

  function renderFlatItem(folder: MediaFolder) {
    const isSelected = folder.id === selectedId
    const { count, label } = getCount(folder)

    return (
      <button
        type="button"
        key={folder.id}
        onClick={() => onSelect(folder.id)}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted/50 transition-colors',
          isSelected && 'bg-primary/10 text-primary',
        )}
      >
        <FolderOpen className={cn('h-3.5 w-3.5 shrink-0', isSelected ? 'text-primary' : 'text-muted-foreground')} />
        <div className="min-w-0 flex-1 text-left">
          <p className="truncate font-medium">{folder.name}</p>
          {folder.parentName && (
            <p className="text-[8px] text-muted-foreground/60 truncate leading-tight">em {folder.parentName}</p>
          )}
        </div>
        {count > 0 && (
          <span className="text-[10px] text-muted-foreground shrink-0">{count} {label}</span>
        )}
        {folder.tags?.slice(0, 2).map(tag => (
          <span key={tag} className="text-[7px] bg-muted px-1 rounded shrink-0 text-muted-foreground">{tag}</span>
        ))}
      </button>
    )
  }

  return (
    <div className={cn('rounded-lg border border-border overflow-hidden', className)}>
      {/* Search */}
      <div className="flex items-center border-b px-3 py-2">
        <Search className="h-3.5 w-3.5 mr-2 text-muted-foreground shrink-0" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar pasta..."
          className="flex-1 text-xs bg-transparent outline-none"
          autoFocus
        />
        {search && (
          <button type="button" onClick={() => setSearch('')} className="text-muted-foreground hover:text-foreground">
            <XIcon className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Tag filter chips */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-1 px-3 py-1.5 border-b">
          <button
            type="button"
            className={cn(
              'text-[10px] px-2 py-0.5 rounded-full transition-colors',
              tagFilter === 'all'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
            onClick={() => setTagFilter('all')}
          >
            Todas
          </button>
          {allTags.map(tag => (
            <button
              type="button"
              key={tag}
              className={cn(
                'text-[10px] px-2 py-0.5 rounded-full border transition-colors',
                tagFilter === tag
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:text-foreground',
              )}
              onClick={() => setTagFilter(tagFilter === tag ? 'all' : tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* Folder tree / flat list */}
      <div className="max-h-[200px] overflow-y-auto py-0.5">
        {isFiltering ? (
          filteredFolders.length === 0 ? (
            <div className="flex flex-col items-center gap-1 py-6 text-center">
              <FolderOpen className="h-5 w-5 text-muted-foreground/20" />
              <p className="text-[10px] text-muted-foreground">Nenhuma pasta encontrada</p>
            </div>
          ) : (
            filteredFolders.map(f => renderFlatItem(f))
          )
        ) : rootFolders.length === 0 ? (
          <div className="flex flex-col items-center gap-1 py-6 text-center">
            <FolderOpen className="h-5 w-5 text-muted-foreground/20" />
            <p className="text-[10px] text-muted-foreground">
              {folders.length === 0 ? 'Nenhuma pasta criada' : 'Nenhuma pasta encontrada'}
            </p>
          </div>
        ) : (
          rootFolders.map(folder => renderTreeItem(folder, 0))
        )}
      </div>

      {/* Count */}
      {folders.length > 0 && (
        <div className="border-t px-3 py-1.5">
          <p className="text-[9px] text-muted-foreground">
            {isFiltering ? `${filteredFolders.length} de ` : ''}{folders.length} pastas
          </p>
        </div>
      )}
    </div>
  )
}
